import { useQuery } from '@tanstack/react-query';
import type { RunTarget } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import { applyTestRunsFilterParams, type TestRunsFilterParams } from './testRunsFilters';

export type TriggeredBySummary = {
  id: string;
  displayName: string | null;
  email: string;
};

export type WorkflowTestExecutionDto = {
  /**
   * Discriminates native Cofactor runs from external-suite runs in the merged
   * runs list. Optional for back-compat with consumers that build this DTO
   * directly; the runs-list API always sets it to `'cofactor'`.
   */
  kind?: 'cofactor';
  id: string;
  status:
    | 'queued'
    | 'running'
    | 'completed'
    | 'completed_with_errors'
    | 'completed_with_inconclusive'
    | 'canceled'
    | 'timeout';
  totalWorkflows: number;
  completedWorkflows: number;
  failedWorkflows: number;
  inconclusiveWorkflows: number;
  successfulWorkflows: number;
  skippedWorkflows: number;
  quarantinedWorkflows: number;
  degradedWorkflows?: number;
  tagFilter?: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  triggeredBy?: TriggeredBySummary | null;
  /** Named suite that produced this run (null for ad-hoc tag/name/all runs). */
  suiteId?: string | null;
  suiteName?: string | null;
  /**
   * What this run was launched against — named suite, named schedule, tag
   * set, or ad hoc. Derived server-side (`deriveRunTarget`) so the list, the
   * filter menu and the trend chart cannot disagree about which series a run
   * belongs to.
   */
  runTarget: RunTarget;
};

/**
 * An external test-suite run (a customer's own Selenium/Playwright suite we ran
 * on their behalf) as it appears in the standard runs list — discriminated by
 * `kind: 'external'`. Counts are per-test verdicts folded across attempts.
 */
export type ExternalRunSummaryDto = {
  kind: 'external';
  id: string;
  status: 'open' | 'collecting' | 'sealed' | 'canceled';
  runnerName: string | null;
  provider: string;
  targetUrl: string | null;
  external: {
    total: number;
    passed: number;
    failed: number;
    flaky: number;
    skipped: number;
  };
  /**
   * The run was cut short and sealed on salvaged results, so the counts above
   * cover only the tests that got to run. Rendered as a "Partial" badge — a
   * truncated run that looks complete is worse than a failed one.
   */
  resultsTruncated: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  /**
   * External runs started from the product surface (a "Run" button against a
   * configured external suite) carry the triggering user and the suite that
   * produced them, just like native runs. External runs that arrived purely
   * via the ingest API (a customer's own CI posting results) carry neither.
   */
  triggeredBy?: TriggeredBySummary | null;
  suiteId?: string | null;
  suiteName?: string | null;
  /** Areas the run fanned out to (renders "N areas"); null before planning. */
  shardTotal: number | null;
  /** Set on a one-off selection — an area re-run names its directory here. */
  selection: { specPath: string | null; grep: string | null };
  rerunOfRunId: string | null;
};

/**
 * A row in the merged runs list: native Cofactor run or external-suite run.
 */
export type RunsListRow = WorkflowTestExecutionDto | ExternalRunSummaryDto;

type PaginationResponse = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type TestExecutionsResponse = {
  ok: boolean;
  data: RunsListRow[];
  pagination: PaginationResponse;
};

export type WorkflowTestExecutionsFetch = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1]
) => ReturnType<typeof fetch>;

type FetchWorkflowTestExecutionsOptions = {
  apiUrl?: string;
  fetchFn?: WorkflowTestExecutionsFetch;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Exclude external-suite runs from the response (native Cofactor runs only). */
  nativeOnly?: boolean;
  /** Target / status / triggeredBy / date-range filters — see `testRunsFilters.ts`. */
  filters?: TestRunsFilterParams;
};

export async function fetchWorkflowTestExecutions({
  apiUrl = API_URL,
  fetchFn = fetch,
  page,
  pageSize,
  sortBy,
  sortOrder,
  nativeOnly,
  filters,
}: FetchWorkflowTestExecutionsOptions): Promise<TestExecutionsResponse> {
  const url = new URL(`${apiUrl}/workflows/test-executions`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('pageSize', String(pageSize));
  if (sortBy) url.searchParams.set('sortBy', sortBy);
  if (sortOrder) url.searchParams.set('sortOrder', sortOrder);
  if (nativeOnly) url.searchParams.set('nativeOnly', '1');
  applyTestRunsFilterParams(url, filters);

  const response = await fetchFn(url.toString(), { credentials: 'include' });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to load agentic runs');
  }
  return (await response.json()) as TestExecutionsResponse;
}

export function useWorkflowTestExecutions(
  orgId: string | null,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Native Cofactor runs only — skips the external-suite merge server-side. */
    nativeOnly?: boolean;
    /** Target / status / triggeredBy / date-range filters — see `testRunsFilters.ts`. */
    filters?: TestRunsFilterParams;
  }
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const sortBy = options?.sortBy;
  const sortOrder = options?.sortOrder;
  const nativeOnly = options?.nativeOnly ?? false;
  const filters = options?.filters;

  return useQuery<TestExecutionsResponse>({
    queryKey: [
      'workflow-test-executions',
      orgId,
      page,
      pageSize,
      sortBy,
      sortOrder,
      nativeOnly,
      filters,
    ],
    enabled: Boolean(orgId),
    queryFn: () =>
      fetchWorkflowTestExecutions({
        page,
        pageSize,
        sortBy,
        sortOrder,
        nativeOnly,
        filters,
      }),
  });
}
